// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {showPremiumActionErrorModal} from '@app/features/app/components/dialogs/components/plutonium/utils/PremiumActionErrorModal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import styles from '@app/features/premium/components/PremiumSubscriptionCard.module.css';
import PremiumState from '@app/features/premium/state/PremiumState';
import {
	OPEN_PAYMENT_PAGE_DESCRIPTOR,
	RECURRING_ELIGIBILITY_DESCRIPTORS,
	RECURRING_TOTAL_DESCRIPTOR,
} from '@app/features/shop/utils/ShopMessageDescriptors';
import {formatTry, formatUsd} from '@app/features/shop/utils/ShopPriceFormat';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {isDesktop, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import Users from '@app/features/user/state/Users';
import WindowState from '@app/features/window/state/Window';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {getFormattedLongDate} from '@fluxer/date_utils/src/DateFormatting';
import type {PaddleSubscriptionState} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {reaction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Your subscription',
	comment: 'Heading of the renewing Plus subscription box in Settings > Plus and on the Plus page.',
});
const STATUS_DESCRIPTORS = {
	active: msg({message: 'Automatic renewal is on', comment: 'Status of a renewing Plus subscription.'}),
	cancel_scheduled: msg({
		message: 'Cancellation scheduled',
		comment: 'Status of a renewing Plus subscription the user cancelled; it stops at the end of the paid period.',
	}),
	past_due: msg({
		message: 'Payment problem',
		comment: 'Status of a renewing Plus subscription whose last renewal payment failed.',
	}),
	paused: msg({message: 'Subscription paused', comment: 'Status of a paused renewing Plus subscription.'}),
	pending: msg({
		message: 'Payment pending',
		comment: 'Status of a renewing Plus subscription whose first payment is not confirmed yet.',
	}),
	canceled: msg({
		message: 'Subscription ended',
		comment: 'Status of a renewing Plus subscription that no longer renews.',
	}),
	needs_review: msg({
		message: 'Subscription under review',
		comment: 'Status of a renewing Plus subscription support has to check before anything can change.',
	}),
} as const;
const PLAN_DESCRIPTOR = msg({message: 'Plan', comment: 'Row label in the subscription box: the amount and period.'});
const PAID_UNTIL_DESCRIPTOR = msg({
	message: 'Paid until',
	comment: 'Row label in the subscription box: the end of the time already paid for.',
});
const NEXT_PAYMENT_DESCRIPTOR = msg({
	message: 'Next payment',
	comment: 'Row label in the subscription box: the date of the next automatic charge.',
});
const ENDS_ON_DESCRIPTOR = msg({
	message: 'Ends on',
	comment: 'Row label in the subscription box: the day a cancelled subscription stops.',
});
const CANCEL_SCHEDULED_NOTE_DESCRIPTOR = msg({
	message: 'No further payments will be taken. {premiumProductName} stays on until {date}.',
	comment: 'Note in the subscription box after the user cancelled. {date} is the end of the paid period.',
});
const PAST_DUE_NOTE_DESCRIPTOR = msg({
	message: "We couldn't take your last payment. Update your payment method to keep your subscription.",
	comment: 'Note in the subscription box when a renewal payment failed.',
});
const NEEDS_REVIEW_NOTE_DESCRIPTOR = msg({
	message: 'We are checking this subscription. If it does not clear up soon, contact support.',
	comment: 'Note in the subscription box when the subscription needs a manual check.',
});
const CANCEL_DESCRIPTOR = msg({
	message: 'Cancel subscription',
	comment: 'Button in the subscription box and in its confirm dialog; stops future renewals.',
});
const KEEP_DESCRIPTOR = msg({
	message: 'Keep subscription',
	comment: 'Button in the cancel confirm dialog that closes it.',
});
const REACTIVATE_DESCRIPTOR = msg({
	message: 'Undo cancellation',
	comment: 'Button in the subscription box after a cancellation; the subscription renews again.',
});
const PAYMENT_METHOD_DESCRIPTOR = msg({
	message: 'Update payment method',
	comment: 'Button in the subscription box; opens the payment page of Paddle in the browser.',
});
const CONFIRM_TITLE_DESCRIPTOR = msg({
	message: 'Cancel automatic renewal?',
	comment: 'Title of the confirm dialog before cancelling a renewing Plus subscription.',
});
const CONFIRM_BODY_DESCRIPTOR = msg({
	message:
		'You will not be charged again. {premiumProductName} stays on until the end of the time you have paid for ({date}).',
	comment: 'Body of the cancel confirm dialog. {date} is the end of the paid period.',
});
const CONFIRM_BODY_NO_DATE_DESCRIPTOR = msg({
	message: 'You will not be charged again. {premiumProductName} stays on for the time you have already paid for.',
	comment: 'Body of the cancel confirm dialog when the end of the paid period is unknown.',
});
const CANCELLED_DESCRIPTOR = msg({
	message: 'Your subscription is canceled. {premiumProductName} stays on until {date}.',
	comment: 'Toast after the server confirmed the cancellation. {date} is the end of the paid period.',
});
const REACTIVATED_DESCRIPTOR = msg({
	message: 'Your subscription renews again.',
	comment: 'Toast after the server confirmed that a cancellation was undone.',
});
const PENDING_DESCRIPTOR = msg({
	message: 'Your request was received. The status will update shortly.',
	comment: 'Toast after a subscription change was sent but the server does not show it yet.',
});
const CANCEL_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't cancel your subscription",
	comment: 'Title of the error dialog when cancelling a renewing Plus subscription fails.',
});
const REACTIVATE_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't undo the cancellation",
	comment: 'Title of the error dialog when undoing the cancellation of a renewing Plus subscription fails.',
});
const PAYMENT_METHOD_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't open the payment page",
	comment: 'Title of the error dialog when the payment method page of Paddle cannot be opened.',
});
const TRY_AGAIN_DESCRIPTOR = msg({
	message: 'Something went wrong. Please try again in a moment.',
	comment: 'Body of the error dialogs of the subscription box.',
});
const CHANGE_REFUSED_DESCRIPTOR = msg({
	message: "This can't be changed right now, for example just before a renewal payment. Please try again later.",
	comment: 'Body of the error dialog when Paddle refuses a subscription change at this moment.',
});

type Action = 'cancel' | 'reactivate' | 'payment_method';

function formatDate(iso: string | null, locale: string): string | null {
	return iso ? getFormattedLongDate(new Date(iso), locale) : null;
}

function failWith(error: unknown, title: MessageDescriptor, dataFlx: string): void {
	const code = failureCode(error);
	const message =
		code === APIErrorCodes.SHOP_SUBSCRIPTION_CHANGE_REFUSED
			? CHANGE_REFUSED_DESCRIPTOR
			: code === APIErrorCodes.PREMIUM_PURCHASE_BLOCKED
				? RECURRING_ELIGIBILITY_DESCRIPTORS.purchase_disabled
				: TRY_AGAIN_DESCRIPTOR;
	showPremiumActionErrorModal(error, {fallbackTitle: title, fallbackMessage: message}, dataFlx);
}

// Micup: the renewing Paddle Plus of this account, in Settings > Plus and on the Plus page. Which actions show is
// the server's call (can_*); success is said only once the server's answer shows the change.
export const PremiumSubscriptionCard = observer(() => {
	const {i18n} = useLingui();
	const subscription = PremiumState.paddleFor(Users.currentUser?.id);
	const [busy, setBusy] = useState(false);
	// On the web the payment page opens from a real link the user clicks: a window opened after the request would be
	// stopped by the pop-up blocker. Kept only until that click.
	const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
	const busyRef = useRef(false);
	const pageOpened = useRef(false);
	// Back from the Paddle page: read the state once, so a changed card or a paid renewal shows.
	useEffect(
		() =>
			reaction(
				() => WindowState.focused && WindowState.visible,
				(back) => {
					if (!back || !pageOpened.current) return;
					pageOpened.current = false;
					void PremiumCommands.refreshPremiumState().catch(() => undefined);
				},
			),
		[],
	);
	if (!subscription) return null;

	const locale = i18n.locale;
	const values = {premiumProductName: PREMIUM_PRODUCT_NAME};
	const paidUntil = formatDate(subscription.paid_until, locale);
	const cancelAt = formatDate(subscription.cancel_at, locale);
	const live = subscription.status === 'active' || subscription.status === 'past_due';
	const nextPayment = live && !cancelAt ? formatDate(subscription.next_billed_at, locale) : null;
	const status =
		subscription.status === 'active' && cancelAt
			? STATUS_DESCRIPTORS.cancel_scheduled
			: STATUS_DESCRIPTORS[subscription.status];
	const amount = (subscription.currency === 'USD' ? formatUsd : formatTry)(subscription.amount_minor, locale);

	const run = async (action: Action): Promise<void> => {
		if (busyRef.current) return;
		busyRef.current = true;
		setBusy(true);
		try {
			if (action === 'payment_method') {
				const url = await PremiumCommands.createPaddlePaymentMethodUrl();
				if (!url.startsWith('https://')) throw new Error('Unexpected payment page address');
				if (isDesktop()) {
					pageOpened.current = true;
					void openExternalUrl(url);
				} else {
					setPaymentUrl(url);
				}
				return;
			}
			let after: PaddleSubscriptionState;
			try {
				after =
					action === 'cancel'
						? await PremiumCommands.cancelPaddleSubscription()
						: await PremiumCommands.reactivatePaddleSubscription();
			} catch (error) {
				failWith(
					error,
					action === 'cancel' ? CANCEL_FAILED_TITLE_DESCRIPTOR : REACTIVATE_FAILED_TITLE_DESCRIPTOR,
					`premium.premium-subscription-card.${action}.error-modal`,
				);
				void PremiumCommands.refreshPremiumState().catch(() => undefined);
				return;
			}
			// The page's own copy of the state follows; the answer above is what decides the message.
			void PremiumCommands.refreshPremiumState().catch(() => undefined);
			const end = formatDate(after.cancel_at, locale);
			if (action === 'cancel' && end) {
				ToastCommands.success(i18n._(CANCELLED_DESCRIPTOR, {...values, date: end}));
			} else if (action === 'reactivate' && !after.cancel_at) {
				ToastCommands.success(i18n._(REACTIVATED_DESCRIPTOR));
			} else {
				ToastCommands.createToast({type: 'info', children: i18n._(PENDING_DESCRIPTOR)});
			}
		} catch (error) {
			failWith(
				error,
				PAYMENT_METHOD_FAILED_TITLE_DESCRIPTOR,
				'premium.premium-subscription-card.payment-method.error-modal',
			);
		} finally {
			busyRef.current = false;
			setBusy(false);
		}
	};

	const confirmCancel = () =>
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(CONFIRM_TITLE_DESCRIPTOR)}
					description={
						paidUntil
							? i18n._(CONFIRM_BODY_DESCRIPTOR, {...values, date: paidUntil})
							: i18n._(CONFIRM_BODY_NO_DATE_DESCRIPTOR, values)
					}
					primaryText={i18n._(CANCEL_DESCRIPTOR)}
					primaryVariant="danger"
					secondaryText={i18n._(KEEP_DESCRIPTOR)}
					onPrimary={() => run('cancel')}
					data-flx="premium.premium-subscription-card.confirm-modal"
				/>
			)),
		);

	const note =
		subscription.status === 'past_due'
			? i18n._(PAST_DUE_NOTE_DESCRIPTOR)
			: subscription.status === 'needs_review'
				? i18n._(NEEDS_REVIEW_NOTE_DESCRIPTOR)
				: cancelAt && subscription.status === 'active'
					? i18n._(CANCEL_SCHEDULED_NOTE_DESCRIPTOR, {...values, date: cancelAt})
					: null;

	return (
		<section
			className={styles.card}
			aria-labelledby="premium-subscription-title"
			data-flx="premium.premium-subscription-card"
		>
			<div className={styles.head} data-flx="premium.premium-subscription-card.head">
				<h2 id="premium-subscription-title" className={styles.title} data-flx="premium.premium-subscription-card.title">
					{i18n._(TITLE_DESCRIPTOR)}
				</h2>
				<p
					className={subscription.status === 'past_due' ? styles.statusWarning : styles.status}
					role="status"
					data-flx="premium.premium-subscription-card.status"
				>
					{i18n._(status)}
				</p>
			</div>
			<dl className={styles.rows} data-flx="premium.premium-subscription-card.rows">
				<div className={styles.row}>
					<dt>{i18n._(PLAN_DESCRIPTOR)}</dt>
					<dd>{i18n._(RECURRING_TOTAL_DESCRIPTOR, {amount, months: subscription.interval_months})}</dd>
				</div>
				{paidUntil && (
					<div className={styles.row}>
						<dt>{i18n._(PAID_UNTIL_DESCRIPTOR)}</dt>
						<dd>{paidUntil}</dd>
					</div>
				)}
				{nextPayment && (
					<div className={styles.row}>
						<dt>{i18n._(NEXT_PAYMENT_DESCRIPTOR)}</dt>
						<dd>{nextPayment}</dd>
					</div>
				)}
				{cancelAt && (
					<div className={styles.row}>
						<dt>{i18n._(ENDS_ON_DESCRIPTOR)}</dt>
						<dd>{cancelAt}</dd>
					</div>
				)}
			</dl>
			{note && (
				<p className={styles.note} data-flx="premium.premium-subscription-card.note">
					{note}
				</p>
			)}
			{(subscription.can_update_payment_method || subscription.can_reactivate || subscription.can_cancel) && (
				<div className={styles.actions} data-flx="premium.premium-subscription-card.actions">
					{subscription.can_update_payment_method && paymentUrl && (
						<a
							href={paymentUrl}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.paymentLink}
							onClick={() => {
								pageOpened.current = true;
								setPaymentUrl(null);
							}}
							data-flx="premium.premium-subscription-card.payment-link"
						>
							{i18n._(OPEN_PAYMENT_PAGE_DESCRIPTOR)}
						</a>
					)}
					{subscription.can_update_payment_method && !paymentUrl && (
						<Button
							variant="secondary"
							small
							disabled={busy}
							onClick={() => void run('payment_method')}
							data-flx="premium.premium-subscription-card.button.payment-method"
						>
							{i18n._(PAYMENT_METHOD_DESCRIPTOR)}
						</Button>
					)}
					{subscription.can_reactivate && (
						<Button
							small
							disabled={busy}
							onClick={() => void run('reactivate')}
							data-flx="premium.premium-subscription-card.button.reactivate"
						>
							{i18n._(REACTIVATE_DESCRIPTOR)}
						</Button>
					)}
					{subscription.can_cancel && (
						<Button
							variant="secondary"
							small
							disabled={busy}
							onClick={confirmCancel}
							data-flx="premium.premium-subscription-card.button.cancel"
						>
							{i18n._(CANCEL_DESCRIPTOR)}
						</Button>
					)}
				</div>
			)}
		</section>
	);
});
